import { isAfter, isValid, parseISO } from "date-fns";
import { localStorageService } from "@entrilia/shared/services/storage";
import type { DescriptiveDateRange } from "@entrilia/shared/utilities/dateFilterHelper";
import { predefinedDateRanges } from "@entrilia/shared/utilities/dateFilterHelper";
import type { FilterType, FilterValue } from "../components/common/filters/filterTypes";

const RECENT_CLIENTS_KEY = "recent_clients";
const MAX_RECENT_CLIENTS = 5;

const getRecentClients = () => localStorageService.getItemWithFallback<string[]>(RECENT_CLIENTS_KEY, []);

const addRecentClient = (clientCode: string) => {
  const recentClients = getRecentClients();
  recentClients.unshift(clientCode);

  const newRecentClients = recentClients.reduce<string[]>((result, code) => {
    if (!result.includes(code) && result.length < MAX_RECENT_CLIENTS) {
      result.push(code);
    }
    return result;
  }, []);

  localStorageService.setItem(RECENT_CLIENTS_KEY, newRecentClients);
};

const BETA_ACCESS_KEY = "beta";
const getBetaAccessEnabled = () => localStorageService.getItem<boolean>(BETA_ACCESS_KEY);
const setBetaAccessEnabled = (value: boolean) => localStorageService.setItem(BETA_ACCESS_KEY, value);

export interface PersistedFilterState {
  filters: PersistedFilter[];
  columns: PersistedColumn[];
  searchValue?: string;
  sorting?: PersistedSortingDefinition;
}

interface PersistedFilter {
  id: string;
  type: FilterType;
  value: FilterValue;
}

interface PersistedColumn {
  id: string;
}

interface PersistedSortingDefinition {
  field: string;
  sort: "asc" | "desc";
}

const getFilterState = (filterId: string) => localStorageService.getItem<PersistedFilterState>(filterId);

const saveFilterState = (filterId: string, filterState: PersistedFilterState) =>
  localStorageService.setItem(filterId, filterState);

const clearFilterState = (filterId: string) => localStorageService.removeItem(filterId);

const CLOSED_MAIN_MENU_ITEMS_KEY = "closed_main_menu_items";
const getClosedMainMenuItems = () => localStorageService.getItemWithFallback<string[]>(CLOSED_MAIN_MENU_ITEMS_KEY, []);
const saveClosedMainMenuItems = (items: string[]) => localStorageService.setItem(CLOSED_MAIN_MENU_ITEMS_KEY, items);

interface StoredDescriptiveDate {
  title: string;
  description?: string;
  from?: string;
  to?: string;
}

const DOC_COLLECTIONS_DATE_RANGE_KEY = "doc_collections_date_range";

const getDocumentCollectionsDateRange = (clientCode: string): DescriptiveDateRange | undefined => {
  const storedDateRange = localStorageService.getItem<StoredDescriptiveDate>(
    `${clientCode}_${DOC_COLLECTIONS_DATE_RANGE_KEY}`
  );

  if (!storedDateRange) {
    return undefined;
  }

  const predefinedRange = predefinedDateRanges.find((r) => r.title === storedDateRange.title);
  if (predefinedRange) {
    return predefinedRange;
  }

  if (storedDateRange.from && storedDateRange.to) {
    const fromDate = parseISO(storedDateRange.from);
    const toDate = parseISO(storedDateRange.to);

    if (isValid(fromDate) && isValid(toDate) && !isAfter(fromDate, toDate)) {
      return {
        title: storedDateRange.title,
        description: storedDateRange.description,
        from: parseISO(storedDateRange.from),
        to: parseISO(storedDateRange.to),
      };
    }
  }

  return undefined;
};

const saveDocumentCollectionsDateRange = (clientCode: string, dateRange: DescriptiveDateRange) => {
  const isCustomRange = predefinedDateRanges.every((r) => r.title !== dateRange.title);

  const storedDateRange: StoredDescriptiveDate = {
    title: dateRange.title,
    description: isCustomRange ? dateRange.description : undefined,
    from: isCustomRange && dateRange.from ? dateRange.from.toISOString() : undefined,
    to: isCustomRange && dateRange.to ? dateRange.to.toISOString() : undefined,
  };

  localStorageService.setItem(`${clientCode}_${DOC_COLLECTIONS_DATE_RANGE_KEY}`, storedDateRange);
};

const storage = {
  getRecentClients,
  addRecentClient,
  getBetaAccessEnabled,
  setBetaAccessEnabled,
  getFilterState,
  saveFilterState,
  clearFilterState,
  getClosedMainMenuItems,
  saveClosedMainMenuItems,
  getDocumentCollectionsDateRange,
  saveDocumentCollectionsDateRange,
};

export default storage;
